import React, { useEffect, useState } from 'react';
import clipboardCross from 'assets/clipboardCross.svg';
import Search, { TFilterChipData } from 'components/Organisms/Search';
import SearchTable from 'components/Organisms/SearchTable';
import useAuthorization from 'hooks/useAuthorization';
import i18n from 'i18n/config';
import { startCase } from 'lodash';
import { UserRole } from 'models/user';
import moment from 'moment';
import { formatNumber } from 'utils/currency.helpers';
import formatIsoDate from 'utils/formatIsoDate';
import mapFxTradeStatusDisplay from 'utils/fxTrades/mapFxTradeStatusDisplay';
import mapTradeTypeToFriendlyName from 'utils/fxTrades/mapTradeTypeToFriendlyName';
import t from 'utils/translationHelper';

import { TradeDto, TradeStatus, TradeType } from '@alpha/fx-dtos';
import { APDatePicker } from '@alpha/ui-lib/ui/DatePicker';
import { Box, Button } from '@alpha/ui-lib/ui/external';
import { Flag } from '@alpha/ui-lib/ui/Flag';
import { Status } from '@alpha/ui-lib/ui/Status';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip';

import ActionDropDown from './DisplayTable/ActionDropDown/ActionDropDown';
import PadColumnValue from './TradeDetail/PadColumnValue/PadColumnValue';
import useStyles from './FxTradeTable.styles';
import useFxTradeTable, { TSearchFilterTypes } from './useFxTradeTable';

export type TFilter = {
  value: string;
  field: string;
  displayValue?: string;
};

interface IFxTradeTable {
  emptyTitle?: string;
  emptySubtitle?: string;
  testId?: string;
  filters?: TFilter[];
  filtersByField?: TSearchFilterTypes;
  handleRemoveFilterItem?(item: TFilter): void;
  lastUpdatedTime?: string;
  tradeTypes?: TradeType[];
  filterButton?: React.ReactNode;
  quickFilterOpenTrades?: () => void;
  quickFilterDrawdownEligible?: () => void;
  valueDate?: string | null;
  setValueDate?: (date: any) => void;
  isMassDrawdownFlowActive?: boolean;
  setSelectedTradeIds?: (ids: string[]) => void;
  selectedTradeIds?: string[];
  setMassdrawdownTrades?: (trades: TradeDto[]) => void;
}

// eslint-disable-next-line max-lines-per-function
const FxTradeTable: React.FC<IFxTradeTable> = (props: IFxTradeTable) => {
  const {
    emptyTitle,
    emptySubtitle,
    testId,
    filters,
    filtersByField,
    handleRemoveFilterItem,
    lastUpdatedTime,
    tradeTypes,
    filterButton,
    valueDate,
    setValueDate,
    isMassDrawdownFlowActive,
    setSelectedTradeIds,
    selectedTradeIds,
    setMassdrawdownTrades,
  } = props;

  const isForwardTable = tradeTypes?.includes(TradeType.FORWARD_FIXED);

  const {
    table,
    handleInputChange,
    searchParams,
    handleTableSortClick,
    reloadTableData,
    columns,
    handleTradeNumberClick,
    accountConfiguration,
    latestValueDateFromTrades,
  } = useFxTradeTable(filtersByField, isMassDrawdownFlowActive);

  useEffect(() => {
    setMassdrawdownTrades?.(table?.items?.items?.records as TradeDto[]);
  }, [table?.items?.items?.records]);

  const [filterChips, setFilterChips] = useState<TFilterChipData[]>([]);
  const [displayMultiSelect, setDisplayMultiSelect] = useState(false);

  const { authorized: canCancelAnyTrade } = useAuthorization([[UserRole.SPOT, UserRole.SPOT_INPUTTER]]);

  const classes = useStyles();

  const generateTableData = () => {
    const records = table?.items?.items?.records as TradeDto[];

    return records?.map((trade) => {
      const tradeValueDateMoment = moment(trade.valueDate);
      const valueDateisSameOrAfterSelectedDate = tradeValueDateMoment.isSameOrAfter(moment(moment(valueDate).format('DD/MM/YYYY'), 'DD/MM/YYYY'));

      let drawdownUnavailableReason;
      if (!trade.drawdownAvailable) {
        drawdownUnavailableReason = trade.drawdownUnavailableReason ?? t('this_trade_is_not_selectable');
      } else if (!valueDateisSameOrAfterSelectedDate) {
        drawdownUnavailableReason = t('value_date_is_before_selected_date');
      }

      return {
        ...trade,
        contractNumber: (
          <Button
            disableRipple
            className={classes.tradeTableButton}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (handleTradeNumberClick) {
                handleTradeNumberClick(trade.id);
              }
            }}
          >
            {isMassDrawdownFlowActive && drawdownUnavailableReason ? (
              <APTooltip title={drawdownUnavailableReason} arrow>
                <div>{trade.contractNumber || '-'}</div>
              </APTooltip>
            )
              : trade.contractNumber || '-'}
          </Button>
        ),
        tradeType: t(mapTradeTypeToFriendlyName(trade.type)) || '-',
        tradeDate: formatIsoDate(trade.date),
        accessDate: formatIsoDate(trade.accessDate),
        soldAmount: (
          <div className={classes.amount}>
            <div>{formatNumber(Number(trade.soldAmount) || 0, 2)}</div>
            <div className={classes.currencyFlag}>
              <Flag code={trade.soldCurrencyCode} size="sm" showCodeLabel />
            </div>
          </div>
        ),
        rate: <span className={classes.tableLink}>{trade.rate ? formatNumber(trade.rate, 4) : '-'}</span>,
        buyAmount: (
          <div className={classes.amount}>
            <div>{formatNumber(Number(trade.buyAmount) || 0, 2)}</div>
            <div className={classes.currencyFlag}>
              <Flag code={trade.buyCurrencyCode} size="sm" showCodeLabel />
            </div>
          </div>
        ),
        buyBalance: (
          <div className={classes.amount}>
            {[formatNumber(trade.buyBalance || 0, 2), trade.buyCurrencyCode].join(
              ' ',
            )}
          </div>
        ),
        valueDate: formatIsoDate(trade.valueDate),
        tradeStatus: (
          <Status
            className={classes.status}
            variant={mapFxTradeStatusDisplay(trade.status).variant}
          >
            {t(mapFxTradeStatusDisplay(trade.status).text) || '-'}
          </Status>
        ),
        padEligibility: (
          <PadColumnValue
            padEligibleItem={trade}
            handleTradeAuthorisation={handleTradeNumberClick}
          />
        ),
        action: (
          <ActionDropDown
            trade={trade}
            setDisplayMultiSelect={setDisplayMultiSelect}
            displayMultiSelect={displayMultiSelect}
            handleReloadTradesTable={reloadTableData}
            showCancelTrade={canCancelAnyTrade}
            eMoneyDisabled={accountConfiguration?.eMoneyDisabled}
          />
        ),
      };
    });
  };

  const mapFiltersToDisplay = (filtersToMap: TFilter[]): TFilterChipData[] => filtersToMap.map((filter) => {
    let filterValue = filter.value as string;
    switch (filter.field) {
      case 'tradeStatus':
      case 'forwardTradeStatus':
        filterValue = startCase(
          mapFxTradeStatusDisplay(
              filter.value as TradeStatus,
          ).text.toLowerCase(),
        );
        break;
      case 'tradeType':
      case 'forwardTradeType':
        filterValue = startCase(
          mapTradeTypeToFriendlyName(filter.value as TradeType).toLowerCase(),
        );
        break;
      case 'tradeStartDate':
      case 'tradeEndDate':
      case 'forwardTradeStartDate':
      case 'forwardTradeEndDate':
      case 'valueStartDate':
      case 'valueEndDate':
      case 'forwardValueStartDate':
      case 'forwardValueEndDate':
      case 'accessDateStartDate':
      case 'accessDateEndDate':
      case 'forwardAccessDateStartDate':
      case 'forwardAccessDateEndDate':
        filterValue = formatIsoDate(filter.value);
        break;
      default:
        filterValue = filter.value;
        break;
    }

    return {
      label: (
        <span>
          <b>
            {t(startCase(filter.displayValue ?? filter.field))}
            :
          </b>
          {' '}
          {t(filterValue)}
        </span>
      ),
      value: filter.value,
      field: filter.field,
    };
  });

  useEffect(() => {
    setFilterChips(mapFiltersToDisplay(filters || []));
  }, [filters]);

  useEffect(() => {
    if (lastUpdatedTime) {
      reloadTableData();
    }
  }, [lastUpdatedTime]);

  const handleRemoveFilter = (item: TFilterChipData): void => {
    const { field, value } = item;
    if (handleRemoveFilterItem) {
      handleRemoveFilterItem({ field, value });
    }
  };

  const shouldDisableMassDrawdownDate = (date: Date) => (
    latestValueDateFromTrades ? (
      date.toISOString() > latestValueDateFromTrades
      || moment(date).isoWeekday() === 7 // Sunday
     || moment(date).isoWeekday() === 6 // Saturday
    ) : false
  );

  const forwardConfig = {
    filters: filterChips,
    totalItems: table.items?.total,
    quickActions: (isMassDrawdownFlowActive ? (
      <>
        <APDatePicker
          fullWidth
          PopoverProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          }}
          language={i18n.language}
          className="datePicker"
          onChange={(value) => {
            setValueDate?.(value);
          }}
          disablePast
          value={valueDate}
          placeholder={t('select_date')}
          emptyLabel={t('select_date')}
          autoOk
          shouldDisableDate={shouldDisableMassDrawdownDate}
          {...(isForwardTable && { label: t('drawdown_value_date') })}
          style={{
            marginTop: isForwardTable ? -16 : 8,
          }}
          InputLabelProps={{
            style: {
              fontSize: '16px',
            },
            shrink: true,
          }}
        />
      </>
    )
      : <></>),
  };

  const spotConfig = {
    filters: filterChips,
    totalItems: table.items?.total,
  };

  const searchConfig = isForwardTable ? forwardConfig : spotConfig;

  const compareTypeFunction = (a: TradeDto, b: TradeDto) => a.currencyPair === b.currencyPair;

  const massDrawdownTableProps = isMassDrawdownFlowActive ? {
    multiSelectable: true,
    keyColumn: 'id',
    checkboxDisabledMessage: t('this_trade_is_not_selectable'),
    setSelectedIds: setSelectedTradeIds,
    selectedIds: selectedTradeIds,
    canOnlySelectSameTypeBaseOnFunction: compareTypeFunction,
    clickable: true,
    disabledSelection: (item: TradeDto) => {
      const tradeValueDateMoment = moment(item.valueDate, 'DD/MM/YYYY');
      const selectedDateMoment = moment(moment(valueDate).format('DD/MM/YYYY'), 'DD/MM/YYYY');
      const valueDateisSameOrAfterSelectedDate = tradeValueDateMoment.isSameOrAfter(selectedDateMoment);
      const accessDate =  moment(item.accessDate, 'DD/MM/YYYY')
      const tradeAccessDateisOnOrBeforeSelectedDate = accessDate.isSameOrBefore(selectedDateMoment);
      
      const validValueDateAndAccessDate = valueDateisSameOrAfterSelectedDate && tradeAccessDateisOnOrBeforeSelectedDate;

      if (isMassDrawdownFlowActive) {
        return (!item.drawdownAvailable || !validValueDateAndAccessDate);
      }
      return false;
    },
    disabledWithOpacity: true,
  } : {};

  const cta = (
    <Box
      display="flex"
      flexDirection="row"
      gridGap="10px"
      flexWrap="wrap"
      alignItems="center"
    >
      <div className={classes.filterMenu}>{filterButton}</div>
    </Box>
  );
  const data = generateTableData();
  return (
    <div data-testid="trades-loader-component" className={[classes.tableWrapper, isMassDrawdownFlowActive ? 'show-action-bar' : null].join(' ')}>
      <div style={{
        display: 'flex', flexGrow: '1', alignItems: 'start', gap: '8px',
      }}
      >
        <Search
          testId="search-trades"
          value={table.searchText}
          placeholder={t('search_by_trade_id')}
          onChange={handleInputChange}
          handleRemoveFilter={handleRemoveFilter}
          style={i18n.language === 'es' ? { width: '370px' } : undefined}
          {...searchConfig}
          loading={table.loading}
        />
        {cta}
      </div>

      <SearchTable
        emptyTable={{
          title: emptyTitle || t('no_fx_trades'),
          subtitle: emptySubtitle || t('you_currently_do_not_have_any_fx_trades'),
          icon: clipboardCross,
        }}
        table={{
          columns,
          data,
          activeColumn: table.sortBy,
          sortOrder: table.sortOrder,
          dataTestId: testId || '',
          keyColumn: 'id',
          totalRowNumber: table.items?.total || 0,
          handleTableSortClick,
          skip: table.skip,
          ...massDrawdownTableProps,
        }}
        loading={table.loading}
        pagination={{
          handleNext: () => table.handleNextPage(searchParams),
          handlePrevious: () => table.handlePreviousPage(searchParams),
          hasNext: Boolean(table.items?.hasNext),
          hasPrevious: Boolean(table.items?.hasPrevious),
        }}
      />

    </div>
  );
};

export default FxTradeTable;
